'use client';

import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import type { SingleValue } from 'react-select';
import type { RootState, AppDispatch } from '@/lib/store';
import { createInquiry } from '@/lib/store/slices/inquirySlice';
import { fetchActiveSettings } from '@/lib/store/slices/settingsSlice';
import { fetchActiveSocialLinks } from '@/lib/store/slices/socialLinkSlice';
import { fetchActiveProjects } from '@/lib/store/slices/projectSlice';
import { DynamicIcon } from '@/components/ui/DynamicIcon';
import { useTranslations } from 'next-intl';
import { validateContact, type ContactFormErrors } from '@/lib/validations/contactSchema';
import { sanitizeObject } from '@/lib/utils/sanitize';
import { checkRateLimit } from '@/lib/utils/rateLimit';
import { trackEvent } from '@/lib/utils/tracking';

interface ContactClientProps { locale: string }

type InquiryType = 'project' | 'job' | 'collaboration' | 'other';

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
  type: InquiryType;
  budget: string;
  projectType: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const initialForm: FormState = {
  name: '', email: '', phone: '', company: '',
  subject: '', message: '', type: 'project', budget: '', projectType: '',
};

export default function ContactClient({ locale }: ContactClientProps) {
  const t = useTranslations('Contact');
  const dispatch = useDispatch<AppDispatch>();
  const { loading }                    = useSelector((s: RootState) => s.inquiries);
  const settings                       = useSelector((s: RootState) => s.settings.currentItem);
  const { items: socialLinks }         = useSelector((s: RootState) => s.socialLinks);
  const { items: projects }            = useSelector((s: RootState) => s.projects);

  useEffect(() => {
    dispatch(fetchActiveSettings());
    dispatch(fetchActiveSocialLinks());
    dispatch(fetchActiveProjects());
  }, [dispatch]);

  const lang          = locale === 'ar' ? 'ar' : 'en';
  const isRtl         = lang === 'ar';
  const isAvailable   = settings?.availableForWork ?? true;
  const availableText = (lang === 'ar' ? settings?.availabilityNote?.ar : settings?.availabilityNote?.en)
                        || t('info.available');

  const [form, setForm]     = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedProject, setSelectedProject] = useState<SingleValue<SelectOption>>(null);
  const [selectedType, setSelectedType]       = useState<SingleValue<SelectOption>>(null);

  const typeOptions = useMemo<SelectOption[]>(() => [
    { value: 'project',       label: t('types.project') },
    { value: 'job',           label: t('types.job') },
    { value: 'collaboration', label: t('types.collaboration') },
    { value: 'other',         label: t('types.other') },
  ], [t]);

  const projectOptions = useMemo<SelectOption[]>(() =>
    projects.map(p => ({
      value: p._id,
      label: lang === 'ar' ? p.title.ar : p.title.en,
    })),
  [projects, lang]);

  // Sync selectedType with initial form value on first render
  useEffect(() => {
    setSelectedType(typeOptions.find(o => o.value === initialForm.type) ?? null);
  }, [typeOptions]);

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const result = validateContact({ ...form, type: (selectedType?.value ?? 'other') as FormState['type'] });
    if (result.success) {
      setErrors({});
      return true;
    }
    setErrors(result.errors);
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (!checkRateLimit('contact-form', 3, 60_000)) {
      setStatus('error');
      return;
    }

    const safe = sanitizeObject({ ...form });

    try {
      await dispatch(createInquiry({
        name: safe.name,
        email: safe.email,
        phone: safe.phone || undefined,
        company: safe.company || undefined,
        subject: safe.subject,
        message: safe.message,
        type: (selectedType?.value as InquiryType) ?? 'other',
        budget: safe.budget || undefined,
        projectType: selectedProject?.label || undefined,
      })).unwrap();
      trackEvent({
          type:     'contact_submit',
          category: 'conversion',
          target:   (selectedType?.value as InquiryType) ?? 'other',
          metadata: { projectType: selectedProject?.label, hasBudget: !!safe.budget },
      });
      setStatus('success');
      setForm(initialForm);
      setSelectedProject(null);
      setSelectedType(typeOptions.find(o => o.value === initialForm.type) ?? null);
    } catch {
      setStatus('error');
    }
  };

  const inputClass = (field: keyof FormState) =>
    `w-full rounded-xl border px-4 py-3 text-sm bg-(--surface) text-(--text) placeholder:text-(--muted)
     focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500
     transition-colors duration-180
     ${errors[field] ? 'border-danger-500' : 'border-(--border)'}`;

  // react-select custom styles using CSS variables
  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'var(--surface)',
      borderColor: state.isFocused ? 'var(--color-primary-500)' : 'var(--border)',
      borderRadius: '0.75rem',
      padding: '4px 4px',
      fontSize: '0.875rem',
      boxShadow: state.isFocused ? '0 0 0 2px rgb(249 115 22 / 0.3)' : 'none',
      '&:hover': { borderColor: 'var(--border)' },
      minHeight: 'unset',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: 'var(--surface)',
      borderRadius: '0.75rem',
      border: '1px solid var(--border)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      overflow: 'hidden',
      zIndex: 50,
    }),
    menuList: (base: any) => ({ ...base, padding: '4px' }),
    option: (base: any, state: any) => ({
      ...base,
      borderRadius: '0.5rem',
      backgroundColor: state.isSelected
        ? 'var(--color-primary-500)'
        : state.isFocused
        ? 'var(--bg)'
        : 'transparent',
      color: state.isSelected ? 'white' : 'var(--text)',
      fontSize: '0.875rem',
      cursor: 'pointer',
    }),
    singleValue: (base: any) => ({ ...base, color: 'var(--text)' }),
    placeholder: (base: any) => ({ ...base, color: 'var(--muted)', fontSize: '0.875rem' }),
    input: (base: any) => ({ ...base, color: 'var(--text)' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({ ...base, color: 'var(--muted)', padding: '4px' }),
    clearIndicator: (base: any) => ({ ...base, color: 'var(--muted)', padding: '4px' }),
    noOptionsMessage: (base: any) => ({ ...base, color: 'var(--muted)', fontSize: '0.875rem' }),
  };

  return (
    <>
      {/* Page hero */}
      <section className="py-16 px-4 sm:px-6 text-center">
        <p className="text-xs font-mono text-primary-500 tracking-widest uppercase mb-3">{t('eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-(--text) mb-4">{t('heading')}</h1>
        <p className="text-lg text-(--muted) max-w-xl mx-auto">{t('desc')}</p>
      </section>

      <section className="section-padding px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-10 lg:gap-16">

          {/* Info panel */}
          <aside className="lg:col-span-2 space-y-5">

            {/* Contact info card */}
            <div className="rounded-2xl border border-(--border) bg-(--surface) p-6 space-y-5">
              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold text-(--muted) uppercase tracking-wider mb-0.5">{t('info.emailLabel')}</p>
                  <a href={`mailto:${settings?.contactEmail || t('info.email')}`} className="text-sm text-primary-500 hover:underline break-all">
                    {settings?.contactEmail || t('info.email')}
                  </a>
                </div>
              </div>

              {/* Phone */}
              {settings?.contactPhone && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-(--muted) uppercase tracking-wider mb-0.5">{t('info.phoneLabel')}</p>
                    <a href={`tel:${settings.contactPhone}`} className="text-sm text-(--text) hover:text-primary-500 transition-colors">
                      {settings.contactPhone}
                    </a>
                  </div>
                </div>
              )}

              {/* Availability */}
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${isAvailable ? 'bg-success-500/10' : 'bg-(--bg)'}`}>
                  {isAvailable ? (
                    <svg className="w-5 h-5 text-success-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-(--muted)" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium ${isAvailable ? 'text-(--text)' : 'text-(--muted)'}`}>
                    {availableText}
                  </p>
                  <p className="text-xs text-(--muted) mt-0.5">{t('info.response')}</p>
                </div>
              </div>
            </div>

            {/* Social links card */}
            {socialLinks.length > 0 && (
              <div className="rounded-2xl border border-(--border) bg-(--surface) p-6">
                <p className="text-xs font-semibold text-(--muted) uppercase tracking-wider mb-4">{t('info.followMe')}</p>
                <div className="flex flex-wrap gap-2">
                  {socialLinks.map((link) => (
                    <a
                      key={link._id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={lang === 'ar' ? link.platform.ar : link.platform.en}
                      title={lang === 'ar' ? link.platform.ar : link.platform.en}
                      className="
                        flex items-center gap-2 px-3 py-2 rounded-lg
                        border border-(--border) bg-(--bg)
                        text-sm text-(--muted)
                        hover:text-primary-500 hover:border-primary-500 hover:bg-primary-500/5
                        transition-all duration-180
                      "
                    >
                      <DynamicIcon
                        iconName={link.icon}
                        size={16}
                        fallback={
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                        }
                      />
                      <span className="text-xs font-medium">{lang === 'ar' ? link.platform.ar : link.platform.en}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            {/* Screen-reader live region — announces status changes immediately */}
            <div aria-live="assertive" aria-atomic="true" className="sr-only">
              {status === 'success' && t('success')}
              {status === 'error'   && t('error')}
              {Object.keys(errors).length > 0 &&
                `${Object.keys(errors).length} ${Object.keys(errors).length === 1 ? 'error' : 'errors'} in the form. Please review and correct.`
              }
            </div>

            {status === 'success' ? (
              <div
                role="status"
                aria-live="polite"
                className="rounded-2xl border border-success-500/30 bg-success-500/5 p-10 text-center space-y-4"
              >
                <div className="w-16 h-16 rounded-full bg-success-500/10 flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-success-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-(--text)">{t('success')}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="text-sm text-primary-500 hover:underline"
                >
                  {t('sendAnother')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-5" aria-label={t('heading')}>
                {/* Row: name + email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="contact-name">{t('name')} *</label>
                    <input
                      id="contact-name" type="text" value={form.name} onChange={set('name')} placeholder={t('name')}
                      aria-invalid={!!errors.name} aria-describedby={errors.name ? 'contact-name-error' : undefined}
                      aria-required="true" className={inputClass('name')}
                    />
                    {errors.name && <p id="contact-name-error" role="alert" className="mt-1 text-xs text-danger-500">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="contact-email">{t('email')} *</label>
                    <input
                      id="contact-email" type="email" value={form.email} onChange={set('email')} placeholder={t('email')}
                      aria-invalid={!!errors.email} aria-describedby={errors.email ? 'contact-email-error' : undefined}
                      aria-required="true" className={inputClass('email')}
                    />
                    {errors.email && <p id="contact-email-error" role="alert" className="mt-1 text-xs text-danger-500">{errors.email}</p>}
                  </div>
                </div>

                {/* Row: phone + company */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="contact-phone">{t('phone')}</label>
                    <input
                      id="contact-phone" type="tel" value={form.phone} onChange={set('phone')} placeholder={t('phone')}
                      aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'contact-phone-error' : undefined}
                      className={inputClass('phone')}
                    />
                    {errors.phone && <p id="contact-phone-error" role="alert" className="mt-1 text-xs text-danger-500">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="contact-company">{t('company')}</label>
                    <input id="contact-company" type="text" value={form.company} onChange={set('company')} placeholder={t('company')} className={inputClass('company')} />
                  </div>
                </div>

                {/* Row: type + budget */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label id="contact-type-label" className="block text-xs font-medium text-(--muted) mb-1.5">{t('type')}</label>
                    <Select<SelectOption>
                      instanceId="contact-type"
                      inputId="contact-type-input"
                      aria-labelledby="contact-type-label"
                      options={typeOptions}
                      value={selectedType}
                      onChange={setSelectedType}
                      isSearchable={false}
                      styles={selectStyles}
                      isRtl={isRtl}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="contact-budget">{t('budget')}</label>
                    <input id="contact-budget" type="text" value={form.budget} onChange={set('budget')} placeholder={t('budget')} className={inputClass('budget')} />
                  </div>
                </div>

                {/* Project select */}
                <div>
                  <label id="contact-project-label" className="block text-xs font-medium text-(--muted) mb-1.5">
                    {t('project')}
                    <span className="text-(--muted) font-normal ms-1">({t('optional')})</span>
                  </label>
                  <Select<SelectOption>
                    instanceId="contact-project"
                    inputId="contact-project-input"
                    aria-labelledby="contact-project-label"
                    options={projectOptions}
                    value={selectedProject}
                    onChange={setSelectedProject}
                    isClearable
                    isSearchable
                    placeholder={t('projectPlaceholder')}
                    noOptionsMessage={() => t('noProjects')}
                    styles={selectStyles}
                    isRtl={isRtl}
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="contact-subject">{t('subject')} *</label>
                  <input
                    id="contact-subject" type="text" value={form.subject} onChange={set('subject')} placeholder={t('subject')}
                    aria-invalid={!!errors.subject} aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
                    aria-required="true" className={inputClass('subject')}
                  />
                  {errors.subject && <p id="contact-subject-error" role="alert" className="mt-1 text-xs text-danger-500">{errors.subject}</p>}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="contact-message">{t('message')} *</label>
                  <textarea
                    id="contact-message"
                    rows={5}
                    value={form.message}
                    onChange={set('message')}
                    placeholder={t('message')}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    aria-required="true"
                    className={`${inputClass('message')} resize-none`}
                  />
                  {errors.message && <p id="contact-message-error" role="alert" className="mt-1 text-xs text-danger-500">{errors.message}</p>}
                </div>

                {status === 'error' && (
                  <p role="alert" className="text-sm text-danger-500">{t('error')}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  className="
                    w-full py-3 rounded-xl bg-primary-500 text-neutral-900 font-semibold text-sm
                    hover:bg-primary-600 active:bg-primary-700
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors duration-180 cursor-pointer
                    focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2
                  "
                >
                  {loading ? t('sending') : t('send')}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map section */}
      <section className="section-padding px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <p className="text-xs font-mono text-primary-500 tracking-widest uppercase mb-1">{t('map.eyebrow')}</p>
            <h2 className="text-2xl font-bold text-(--text)">{t('map.heading')}</h2>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-(--border) h-80 sm:h-96">
            <iframe
              title="Location Map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=31.0%2C29.9%2C31.5%2C30.2&amp;layer=mapnik&amp;marker=30.0626%2C31.2497"
              className="w-full h-full"
              style={{ border: 0, filter: 'contrast(0.95) saturate(0.9)' }}
              loading="lazy"
              allowFullScreen
            />
            {/* Info overlay */}
            <div className="absolute bottom-4 inset-s-4">
              <div className="rounded-xl border border-(--border) bg-(--surface)/90 backdrop-blur-sm px-4 py-3 shadow-lg">
                <p className="text-sm font-semibold text-(--text)">{t('map.city')}</p>
                <p className="text-xs text-(--muted) mt-0.5">{t('map.timezone')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
